// Iter-194o — Drill-down dialog for Drive Health KPI cards.
//
// When the CEO clicks a warn/danger KPI card (Files >15d pending, Clients à
// régulariser, Documents administratifs), this dialog opens listing the
// concrete items behind the count (names, identifiers, mission/client
// context). Each row can be clicked to jump straight to the corresponding
// wizard preselected on that exact entry.
//
// The dialog is a single component reused for the three KPIs. The behavior
// of "click row" is controlled by the `kind` prop.
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, FileText, ChevronRight, FolderOpen, FolderArchive, HardDrive, Building2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CONFIG = {
  "files-pending": {
    endpoint: "/admin/drive-health/files-pending",
    title: "Fichiers locaux en attente de purge",
    description: "Bons de commande et factures > seuil de purge encore sur le disque. Aucune action de wizard — surfaces simplement les références.",
    icon: HardDrive,
    listKey: "items",
    rowAction: null, // no wizard for this kind
  },
  "clients-no-drive": {
    endpoint: "/admin/drive-migration/pending",
    title: "Clients à régulariser",
    description: "Clients sans dossier Drive lié ou avec une arborescence incomplète. Cliquez sur un client pour l'ouvrir dans le wizard.",
    icon: FolderOpen,
    listKey: null, // root array
    rowAction: "drive-migration-open",
  },
  "admin-docs": {
    endpoint: "/admin/drive-migration/admin-docs-pending",
    title: "Documents administratifs à migrer",
    description: "Clients dont les documents administratifs vivent encore hors de `005-Documents Administratifs`. Cliquez sur un client pour l'ouvrir dans le wizard.",
    icon: FolderArchive,
    listKey: null,
    rowAction: "admin-docs-migration-open",
  },
};

export default function DriveHealthDetailsDialog({ open, kind, onClose }) {
  const cfg = CONFIG[kind];
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    if (!open || !cfg) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(cfg.endpoint);
        if (cancelled) return;
        if (cfg.listKey) {
          setRows(r.data?.[cfg.listKey] || []);
          const { [cfg.listKey]: _list, ...rest } = r.data || {};
          setMeta(rest);
        } else {
          setRows(Array.isArray(r.data) ? r.data : []);
          setMeta(null);
        }
      } catch (_e) {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, kind]);

  if (!cfg) return null;
  const Icon = cfg.icon;

  const handleRowClick = (row) => {
    if (!cfg.rowAction) return;
    window.dispatchEvent(new CustomEvent(cfg.rowAction, {
      detail: { client_id: row.client_id || null },
    }));
    onClose && onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid={`drive-health-details-${kind}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon size={18} className="text-zinc-700" />
            {cfg.title}
          </DialogTitle>
          <DialogDescription>{cfg.description}</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-zinc-500 py-8 justify-center">
            <Loader2 size={16} className="animate-spin" /> Chargement…
          </div>
        )}

        {!loading && rows.length === 0 && (
          <div className="py-8 text-center text-sm text-zinc-500">
            Rien à afficher — la pile est vide.
          </div>
        )}

        {!loading && rows.length > 0 && (
          <div className="max-h-[60vh] overflow-y-auto border border-zinc-200 rounded-md divide-y divide-zinc-100" data-testid={`drive-health-details-list-${kind}`}>
            {rows.map((r, i) => (
              <RowItem
                key={(r.client_id || r.id || i) + "-" + i}
                kind={kind}
                row={r}
                clickable={!!cfg.rowAction}
                onClick={() => handleRowClick(r)}
              />
            ))}
          </div>
        )}

        {/* Meta footer for files-pending */}
        {meta && (
          <div className="text-[11px] text-zinc-500 font-mono pt-2">
            Seuil de purge : <b>{meta.purge_age_days} j</b> · BC : <b>{meta.n_purchase_orders}</b> · Factures : <b>{meta.n_invoices}</b>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RowItem({ kind, row, clickable, onClick }) {
  const base = "px-3 py-2 flex items-center gap-3 text-sm";
  const cls = clickable ? `${base} hover:bg-zinc-50 cursor-pointer transition-colors` : base;
  const Wrapper = clickable ? "button" : "div";

  // Render shape varies by kind.
  let primary = "—";
  let secondary = "";
  let badge = null;
  if (kind === "files-pending") {
    primary = row.kind === "purchase_order"
      ? `BC ${row.number || row.id}`
      : `Facture ${row.number || row.id}`;
    secondary = [
      row.client_name,
      row.mission_title,
      row.year && row.month ? `${row.year}-${String(row.month).padStart(2, "0")}` : null,
      row.has_drive_copy ? "Copie Drive OK" : "Pas de copie Drive",
    ].filter(Boolean).join(" · ");
    badge = row.kind === "purchase_order" ? (
      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">BC</span>
    ) : (
      <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">FAC</span>
    );
  } else if (kind === "clients-no-drive") {
    primary = row.name || row.client_id;
    secondary = [
      row.country,
      row.missions_count ? `${row.missions_count} mission(s)` : null,
      row.is_subcontractor ? "sous-traitant" : null,
    ].filter(Boolean).join(" · ");
    badge = (
      <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
        row.reason === "MISSING_FOLDER" ? "bg-red-100 text-red-800"
        : row.reason === "INCOMPLETE_SUBTREE" ? "bg-amber-100 text-amber-800"
        : "bg-zinc-100 text-zinc-700"
      }`}>
        {row.reason === "MISSING_FOLDER" ? "Sans Drive"
          : row.reason === "INCOMPLETE_SUBTREE" ? "Arbo incomplète"
          : row.reason === "MISSING_ADMIN_SUBFOLDER" ? "005 absent"
          : "À régulariser"}
      </span>
    );
  } else if (kind === "admin-docs") {
    primary = row.name || row.client_id;
    secondary = `${row.n_docs_drive_to_move} Drive · ${row.n_docs_local_to_upload} locaux · ${row.n_docs_total} total`;
    badge = (
      <span className={`text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ${
        row.has_admin_subfolder ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"
      }`}>
        {row.has_admin_subfolder ? "Docs à déplacer" : "005 absent"}
      </span>
    );
  }

  return (
    <Wrapper
      type={clickable ? "button" : undefined}
      onClick={clickable ? onClick : undefined}
      className={cls + (clickable ? " w-full text-left" : "")}
      data-testid={`drive-health-details-row-${row.client_id || row.id || row.invoice_id}`}
    >
      <Building2 size={14} className="text-zinc-400 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-zinc-900 truncate flex items-center gap-2">
          {badge}
          <span className="truncate">{primary}</span>
        </div>
        {secondary && <div className="text-[11px] text-zinc-500 truncate">{secondary}</div>}
      </div>
      {clickable && <ChevronRight size={14} className="text-zinc-400 shrink-0" />}
    </Wrapper>
  );
}
